import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./sofp.css";

const todayISO = () => new Date().toISOString().split("T")[0];

const money = (value) =>
  `₦${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const normaliseType = (type) => String(type || "").trim();

const sumAmts = (rows) =>
  rows.reduce((sum, row) => sum + Number(row.amt || 0), 0);

const isMaterial = (value) => Math.abs(Number(value || 0)) >= 0.01;

const BalanceSheet = () => {
  const { companyId } = useCompany();

  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [companyStartDate, setCompanyStartDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [bsData, setBsData] = useState({
    assetsTotal: 0,
    liabilitiesTotal: 0,
    equityTotal: 0,
    liabilitiesAndEquityTotal: 0,
    checkDifference: 0,
    assets: [],
    liabilities: [],
    equity: [],
    profitOrLoss: 0,
  });

  const [expanded, setExpanded] = useState({
    assets: true,
    liabilities: true,
    equity: true,
  });

  const fetchCompanyStartDate = useCallback(async () => {
    if (!companyId) return;

    try {
      const { data, error } = await supabase
        .from("journal_entries")
        .select("date")
        .eq("company_id", companyId)
        .order("date", { ascending: true })
        .limit(1);

      if (error) throw error;

      setCompanyStartDate(data?.[0]?.date || "");
    } catch (e) {
      console.warn("Could not fetch company start date:", e);
      setCompanyStartDate("");
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompanyStartDate();
  }, [fetchCompanyStartDate]);

  const fetchBalanceSheet = useCallback(async () => {
    if (!companyId || !asOfDate) return;

    setLoading(true);
    setErr("");

    try {
      const { data: rows, error } = await supabase.rpc(
        "get_balance_sheet_as_at",
        {
          p_company_id: companyId,
          p_as_of_date: asOfDate,
        }
      );

      if (error) throw error;

      const accounts = (rows || []).map((row) => {
        const debit = Number(row.total_debit || 0);
        const credit = Number(row.total_credit || 0);
        const type = normaliseType(row.account_type);

        return {
          id: row.account_id,
          name: row.account_name || "Unknown Account",
          type,
          debit,
          credit,
        };
      });

      const assets = [];
      const liabilities = [];
      const equity = [];

      let revenueTotal = 0;
      let costOfSalesTotal = 0;
      let expenseTotal = 0;

      accounts.forEach((account) => {
        const { type, debit, credit } = account;

        if (type === "Asset") {
          const amt = debit - credit;
          if (isMaterial(amt)) assets.push({ name: account.name, amt });
          return;
        }

        if (type === "Liability") {
          const amt = credit - debit;
          if (isMaterial(amt)) liabilities.push({ name: account.name, amt });
          return;
        }

        if (type === "Equity") {
          const amt = credit - debit;
          if (isMaterial(amt)) equity.push({ name: account.name, amt });
          return;
        }

        if (type === "Revenue") {
          revenueTotal += credit - debit;
          return;
        }

        if (type === "Cost of Sales") {
          costOfSalesTotal += debit - credit;
          return;
        }

        if (type === "Expense") {
          expenseTotal += debit - credit;
        }
      });

      const profitOrLoss = revenueTotal - costOfSalesTotal - expenseTotal;

      if (isMaterial(profitOrLoss)) {
        equity.push({
          name:
            profitOrLoss >= 0
              ? "Accumulated Profit / Retained Earnings"
              : "Accumulated Loss",
          amt: profitOrLoss,
          isSystemGenerated: true,
        });
      }

      const assetsTotal = sumAmts(assets);
      const liabilitiesTotal = sumAmts(liabilities);
      const equityTotal = sumAmts(equity);
      const liabilitiesAndEquityTotal = liabilitiesTotal + equityTotal;
      const checkDifference = assetsTotal - liabilitiesAndEquityTotal;

      setBsData({
        assetsTotal,
        liabilitiesTotal,
        equityTotal,
        liabilitiesAndEquityTotal,
        checkDifference,
        assets,
        liabilities,
        equity,
        profitOrLoss,
      });
    } catch (e) {
      console.error("Balance Sheet fetch error:", e);
      setErr(e?.message || "Failed to compute balance sheet.");
    } finally {
      setLoading(false);
    }
  }, [companyId, asOfDate]);

  useEffect(() => {
    fetchBalanceSheet();
  }, [fetchBalanceSheet]);

  const isBalanced = useMemo(
    () => Math.abs(Number(bsData.checkDifference || 0)) < 0.01,
    [bsData.checkDifference]
  );

  const toggle = (section) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.text(`Statement of Financial Position`, 10, 12);
    doc.text(`As at ${asOfDate}`, 10, 20);

    const body = [
      ["ASSETS", ""],
      ...bsData.assets.map((item) => [item.name, money(item.amt)]),
      ["Total Assets", money(bsData.assetsTotal)],
      ["", ""],
      ["LIABILITIES", ""],
      ...bsData.liabilities.map((item) => [item.name, money(item.amt)]),
      ["Total Liabilities", money(bsData.liabilitiesTotal)],
      ["", ""],
      ["EQUITY", ""],
      ...bsData.equity.map((item) => [item.name, money(item.amt)]),
      ["Total Equity", money(bsData.equityTotal)],
      ["", ""],
      ["Total Liabilities + Equity", money(bsData.liabilitiesAndEquityTotal)],
      ["Check Difference", money(bsData.checkDifference)],
    ];

    doc.autoTable({
      head: [["Description", "Amount"]],
      body,
      startY: 28,
      theme: "striped",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 128, 128] },
    });

    doc.save(`statement_of_financial_position_${asOfDate}.pdf`);
  };

  return (
    <div className="sofp-container">
      <div className="sofp-header">
        <div>
          <h2 className="sofp-title">Statement of Financial Position</h2>
          <p className="sofp-sub">
            Balance Sheet as at <strong>{asOfDate}</strong>
          </p>
          <p className="sofp-sub">
            Report basis: from{" "}
            <strong>{companyStartDate || "first company journal entry"}</strong>{" "}
            to <strong>{asOfDate}</strong>
          </p>
        </div>

        <button onClick={exportToPDF} className="sofp-primary">
          Export to PDF
        </button>
      </div>

      <div className="sofp-filter-card">
        <div className="sofp-filter-row">
          <div className="input-group">
            <label>Reporting Date</label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
            <small>
              A balance sheet is prepared as at a specific date. KostPadi
              automatically includes all company transactions from inception up
              to this date.
            </small>
          </div>

          <div className="input-group">
            <label>&nbsp;</label>
            <button onClick={fetchBalanceSheet} className="sofp-primary">
              Run Report
            </button>
          </div>
        </div>

        {err && <p className="sofp-error">{err}</p>}
      </div>

      <div className={`sofp-check-card ${isBalanced ? "ok" : "warn"}`}>
        <div>
          <strong>{isBalanced ? "Balanced" : "Out of Balance"}</strong>
          <p>
            Assets must equal Liabilities + Equity. Difference:{" "}
            <strong>{money(bsData.checkDifference)}</strong>
          </p>
        </div>
      </div>

      <div className="sofp-card">
        {loading ? (
          <div className="sofp-loading">Loading balance sheet…</div>
        ) : (
          <table className="sofp-table">
            <tbody>
              <tr className="group-header" onClick={() => toggle("assets")}>
                <td>Total Assets</td>
                <td className="amt">{money(bsData.assetsTotal)}</td>
                <td className="toggle-icon">{expanded.assets ? "▾" : "▸"}</td>
              </tr>

              {expanded.assets &&
                bsData.assets.map((item, index) => (
                  <tr key={`asset-${index}`} className="breakdown">
                    <td>{item.name}</td>
                    <td className="amt">{money(item.amt)}</td>
                    <td />
                  </tr>
                ))}

              <tr
                className="group-header"
                onClick={() => toggle("liabilities")}
              >
                <td>Total Liabilities</td>
                <td className="amt">{money(bsData.liabilitiesTotal)}</td>
                <td className="toggle-icon">
                  {expanded.liabilities ? "▾" : "▸"}
                </td>
              </tr>

              {expanded.liabilities &&
                bsData.liabilities.map((item, index) => (
                  <tr key={`liability-${index}`} className="breakdown">
                    <td>{item.name}</td>
                    <td className="amt">{money(item.amt)}</td>
                    <td />
                  </tr>
                ))}

              <tr className="group-header" onClick={() => toggle("equity")}>
                <td>Total Equity</td>
                <td className="amt">{money(bsData.equityTotal)}</td>
                <td className="toggle-icon">{expanded.equity ? "▾" : "▸"}</td>
              </tr>

              {expanded.equity &&
                bsData.equity.map((item, index) => (
                  <tr key={`equity-${index}`} className="breakdown">
                    <td>
                      {item.name}
                      {item.isSystemGenerated && (
                        <small className="sofp-system-note">
                          {" "}
                          — system-calculated from profit/loss accounts
                        </small>
                      )}
                    </td>
                    <td className="amt">{money(item.amt)}</td>
                    <td />
                  </tr>
                ))}

              <tr className="group-total final">
                <td>Liabilities + Equity</td>
                <td className="amt">
                  {money(bsData.liabilitiesAndEquityTotal)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BalanceSheet;
